import React, { useState, useEffect } from 'react';
import './DictationDetailsOverlay.css';

const DictationDetailsOverlay = ({ article, isOpen, onClose, userStats }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock data for demonstration - in real app, this would come from API
  const mockStats = {
    totalSentences: 5,
    uniqueWords: 37,
    lastAttemptMistakes: 6,
    improvement: {
      firstAttempt: {
        date: 'Aug 16, 2025',
        mistakes: 12
      },
      latestAttempt: {
        date: 'Aug 17, 2025',
        mistakes: 6
      },
      percentage: 50
    },
    mistakes: [
      {
        wrong: 'becouse',
        correct: 'because',
        meaning: 'for the reason that'
      },
      {
        wrong: 'goverment',
        correct: 'government',
        meaning: 'the system that rules a nation'
      },
      {
        wrong: 'recieve',
        correct: 'receive',
        meaning: 'to get or be given something'
      }
    ],
    schedule: {
      nextPractice: 'Aug 20, 2025',
      lastAttempt: 'Aug 17, 2025'
    }
  };

  useEffect(() => {
    if (isOpen) {
      // In real app, fetch stats from API
      setLoading(true);
      setTimeout(() => {
        setStats(mockStats);
        setLoading(false);
      }, 500);
    }
  }, [isOpen]);

  const handleRetry = () => {
    onClose();
    // Trigger retry functionality
  };

  const handleMarkCompleted = () => {
    // Mark dictation as completed
    console.log('Marked as completed');
  };

  const handleExportMistakes = () => {
    // Export mistakes functionality
    console.log('Exporting mistakes');
  };

  if (!isOpen) return null;

  return (
    <div className="dictation-details-overlay">
      <div className="dictation-details-content">
        {/* Header */}
        <div className="details-header">
          <h2>🎤 Dictation Details Overlay</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>

        {loading ? (
          <div className="loading-section">
            <i className="material-icons loading-icon">hourglass_empty</i>
            <span>Loading dictation details...</span>
          </div>
        ) : error ? (
          <div className="error-section">
            <i className="material-icons error-icon">error</i>
            <span>{error}</span>
          </div>
        ) : (
          <div className="details-content">
            {/* Dictation Exercise Stats */}
            <div className="stats-section">
              <h3>🎧 Dictation Exercise Stats</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-icon">📝</div>
                  <div className="stat-info">
                    <div className="stat-label">Total Sentences</div>
                    <div className="stat-value">{stats.totalSentences}</div>
                  </div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">🆔</div>
                  <div className="stat-info">
                    <div className="stat-label">Unique Words</div>
                    <div className="stat-value">{stats.uniqueWords}</div>
                  </div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">❓</div>
                  <div className="stat-info">
                    <div className="stat-label">Mistakes in Last Attempt</div>
                    <div className="stat-value">{stats.lastAttemptMistakes}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Improvement Tracking */}
            <div className="improvement-section">
              <h3>📈 Improvement Tracking</h3>
              <div className="improvement-timeline">
                <div className="timeline-item first">
                  <div className="timeline-date">{stats.improvement.firstAttempt.date}</div>
                  <div className="timeline-mistakes">{stats.improvement.firstAttempt.mistakes} mistakes</div>
                  <div className="timeline-label">First Dictation</div>
                </div>
                
                <div className="timeline-arrow">→</div>
                
                <div className="timeline-item latest">
                  <div className="timeline-date">{stats.improvement.latestAttempt.date}</div>
                  <div className="timeline-mistakes">{stats.improvement.latestAttempt.mistakes} mistakes</div>
                  <div className="timeline-label">Latest Dictation</div>
                </div>
              </div>
              
              <div className="improvement-summary">
                <span className="improvement-text">➡️ Improvement: {stats.improvement.percentage}% fewer mistakes</span>
              </div>
            </div>

            {/* Mistakes & Corrections */}
            <div className="mistakes-section">
              <h3>🔡 Mistakes & Corrections</h3>
              <div className="mistakes-table">
                <div className="table-header">
                  <div className="header-cell">❌ Misspelled Word</div>
                  <div className="header-cell">✅ Correct Form</div>
                  <div className="header-cell">📖 Meaning</div>
                </div>
                {stats.mistakes.map((mistake, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell wrong-word">{mistake.wrong}</div>
                    <div className="table-cell correct-word">{mistake.correct}</div>
                    <div className="table-cell meaning">{mistake.meaning}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule & Review */}
            <div className="schedule-section">
              <h3>⏳ Schedule & Review</h3>
              <div className="schedule-info">
                <div className="schedule-item">
                  <span className="schedule-icon">🎯</span>
                  <span className="schedule-label">Next Suggested Practice:</span>
                  <span className="schedule-value">{stats.schedule.nextPractice}</span>
                </div>
                <div className="schedule-item">
                  <span className="schedule-icon">📅</span>
                  <span className="schedule-label">Last Attempt:</span>
                  <span className="schedule-value">{stats.schedule.lastAttempt}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="actions-section">
              <h3>⚡ Quick Actions</h3>
              <div className="action-buttons">
                <button className="action-btn retry-btn" onClick={handleRetry}>
                  <i className="material-icons">replay</i>
                  🔁 Retry Dictation
                </button>
                
                <button className="action-btn complete-btn" onClick={handleMarkCompleted}>
                  <i className="material-icons">check_circle</i>
                  ✅ Mark as Completed
                </button>
                
                <button className="action-btn export-btn" onClick={handleExportMistakes}>
                  <i className="material-icons">file_download</i>
                  📤 Export Mistakes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DictationDetailsOverlay;
